import React, { useMemo } from 'react';

import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { useOrganizationSitesV2 } from '@apis/rendall/queries/use-organization-sites-v2';
import { useScannerProfile } from '@apis/rendall/queries/use-scanner-profile';
import { ControlledSelectV2 } from '@packages/components/controls/controlled-select-v2/controlled-select-v2';
import { ControlledTextFieldV2 } from '@packages/components/controls/controlled-text-field-v2/controlled-text-field-v2';
import {
  Grid,
  LinkRouter,
  Skeleton,
  Stack,
  Typography,
} from '@packages/fenrir/components';
import type { Option } from '@packages/utils/create-options/create-options';

import type { ScannerDetailsForm } from '../../pages/scanners/pages/scanners-config/components/types';

import { useGetEditableStatus } from './use-get-editable-status/use-get-editable-status';

const TextSkeleton = () => <Skeleton variant="text" sx={{ width: '10rem' }} />;

export const ScannerInformationV2 = () => {
  const { t } = useTranslation('telemetryScannerConfig');
  const { scannerId = '' } = useParams();
  const { data: scannerProfile, isPending } = useScannerProfile(scannerId);
  const { control, watch } = useFormContext<ScannerDetailsForm>();
  const formData = watch();

  const { data: sites } = useOrganizationSitesV2();
  const options = useMemo<Option[]>(
    () =>
      sites?.map(({ name, uuid }) => ({
        label: name ?? '',
        value: uuid ?? '',
      })) ?? [],
    [sites],
  );
  const isEditable = useGetEditableStatus(scannerProfile?.scannerStatus);

  return (
    <Stack
      data-testid="main-scanner-info-column"
      gap={2}
      width="100%"
      marginBottom={3}
    >
      <Typography variant="h4">{t('scannerInformation.title')}</Typography>
      <Grid container width="100%" gap={2} spacing={1}>
        <Grid size={{ xs: 6, md: 4, lg: 3 }}>
          <ControlledSelectV2
            controllerProps={{
              control,
              name: 'deviceType',
            }}
            label={t('scannerInformation.scannerType')}
            options={[
              {
                value: formData.deviceType,
                label: t(`scannerInformation.types.${formData.deviceType}`),
              },
            ]}
            required
            disabled={true}
            sx={{ width: '100%' }}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 4, lg: 3 }}>
          <ControlledSelectV2
            controllerProps={{
              control,
              name: 'model',
            }}
            label={t('scannerInformation.model')}
            options={[
              {
                value: formData.model,
                label: formData.model
                  ? t(`telemetryDevices:models.${formData.model}`)
                  : '',
              },
            ]}
            required
            disabled={true}
            sx={{ width: '100%' }}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 10, lg: 3 }} alignSelf="flex-end">
          <LinkRouter
            isExternal
            variant="body1"
            color="primary"
            to={t('scannerInstallationGuideLink')}
          >
            {t('scannerDocumentation')}
          </LinkRouter>
        </Grid>
        <Grid size={{ xs: 6, md: 4, lg: 3 }}>
          <ControlledTextFieldV2
            controllerProps={{
              control,
              name: 'name',
            }}
            required
            disabled={!isEditable}
            sx={{ width: '100%' }}
            label={t('scannerInformation.scannerName')}
            outlinedInputProps={{
              id: 'name',
            }}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 4, lg: 3 }}>
          <ControlledSelectV2
            label={t('labels.site')}
            controllerProps={{
              control,
              name: 'site',
            }}
            disabled={!isEditable}
            sx={{ width: '100%' }}
            options={options}
            required
          />
        </Grid>
      </Grid>
      <Stack
        direction="row"
        gap={2}
        marginTop={3}
        justifyContent="flex-start"
        width="90%"
        flexWrap="wrap"
      >
        <Stack gap={1}>
          <Stack direction="row" gap={1}>
            <Typography variant="body2" fontWeight="bold">
              {t('scannerInformation.scannerUuid')}:
            </Typography>
            {isPending ? (
              <TextSkeleton />
            ) : (
              <Typography variant="body2">{formData.uuid}</Typography>
            )}
          </Stack>
          <Stack direction="row" gap={1}>
            <Typography variant="body2" fontWeight="bold">
              {t('scannerInformation.featureFlagged.ip')}:
            </Typography>
            {isPending ? (
              <TextSkeleton />
            ) : (
              <Typography variant="body2">
                {scannerProfile?.networkConfiguration?.ipAddress ?? ''}
              </Typography>
            )}
          </Stack>
          <Stack direction="row" gap={1}>
            <Typography variant="body2" fontWeight="bold">
              {t('scannerInformation.subnetMask')}:
            </Typography>
            {isPending ? (
              <TextSkeleton />
            ) : (
              <Typography variant="body2">
                {scannerProfile?.networkConfiguration?.subnetMask ?? ''}
              </Typography>
            )}
          </Stack>
          <Stack direction="row" gap={1}>
            <Typography variant="body2" fontWeight="bold">
              {t('scannerInformation.version')}:
            </Typography>
            {isPending ? (
              <TextSkeleton />
            ) : (
              <Typography variant="body2">
                {scannerProfile?.scannerDetails?.version ?? ''}
              </Typography>
            )}
          </Stack>
        </Stack>
        <Stack gap={1}>
          <Stack direction="row" gap={1}>
            <Typography variant="body2" fontWeight="bold">
              {t('scannerInformation.serialNumber')}:
            </Typography>
            {isPending ? (
              <TextSkeleton />
            ) : (
              <Typography variant="body2">
                {scannerProfile?.serialNumber ?? ''}
              </Typography>
            )}
          </Stack>
          <Stack direction="row" gap={1}>
            <Typography variant="body2" fontWeight="bold">
              {t('scannerInformation.gateWay')}:
            </Typography>
            {isPending ? (
              <TextSkeleton />
            ) : (
              <Typography variant="body2">
                {scannerProfile?.networkConfiguration?.gateway ?? ''}
              </Typography>
            )}
          </Stack>
          <Stack direction="row" gap={1}>
            <Typography variant="body2" fontWeight="bold">
              {t('scannerInformation.deploymentId')}:
            </Typography>
            {isPending ? (
              <TextSkeleton />
            ) : (
              <Typography variant="body2">
                {scannerProfile?.deploymentID ?? ''}
              </Typography>
            )}
          </Stack>
          <Stack direction="row" gap={1}>
            <Typography variant="body2" fontWeight="bold">
              {t('scannerInformation.lastScanCompletion')}:
            </Typography>
            {isPending ? (
              <TextSkeleton />
            ) : (
              <Typography variant="body2">
                {scannerProfile?.scannerDetails?.lastOpenVASScanTimestamp ?? ''}
              </Typography>
            )}
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};
